import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Dot,
  Area,
  ComposedChart,
  Brush
} from 'recharts';
import {
  Settings as SettingsIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Warning
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { niceTicks } from '../utils/chartTicks';
import { setUnitSystem, unitLabels } from '../utils/units';
import CalMeterInsights from './CalMeterInsights';
import RegressionDiagram from './RegressionDiagram';

interface MeterFactorData {
  proving_timestamp: string;
  meter_factor: number;
  report_id: string;
  deviation_from_previous?: number;
  floating_ucl?: number;  // Per-point floating UCL
  floating_lcl?: number;  // Per-point floating LCL
  floating_ccl?: number;  // Per-point Control Center Line
}

interface ControlLimits {
  ucl: number;
  lcl: number;
  center: number;
  ccl?: number;  // Control Center Line for API MPMS 13.2
  uncertainty_range?: number;  // API MPMS 13.2
  t_value?: number;  // t-distribution value
  control_range_group?: number;  // Which group of proofs we're in
  evaluation_range_size?: number;  // How many points used
  control_range_size?: number;  // Update frequency
}

interface ChartStatistics {
  fixed_control_limits: ControlLimits;
  floating_control_limits: ControlLimits;
  moving_average: number[];
  drift_detected: boolean;
  drift_direction?: 'increasing' | 'decreasing' | 'stable';
  total_provings: number;
}

interface ChartSettings {
  control_limit_type: 'fixed' | 'floating' | 'both';
  control_limit_multiplier: number;
  moving_average_window: number;
  evaluation_range_days: number;
  proving_frequency_days: number;
  show_individual_points: boolean;
  show_moving_average: boolean;
  show_control_limits: boolean;
  average_annual_flow?: number;  // BBL/year
  show_regression: boolean;
  show_prediction_intervals: boolean;
  show_confidence_intervals: boolean;
}

interface MeterFactorChartProps {
  meterProductId: string;
  meterName?: string;
  productType?: string;
  onClose?: () => void;
}

const MeterFactorChart: React.FC<MeterFactorChartProps> = ({
  meterProductId,
  meterName,
  productType,
  onClose
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [chartData, setChartData] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<ChartStatistics | null>(null);
  const [settings, setSettings] = useState<ChartSettings>({
    control_limit_type: 'both',
    control_limit_multiplier: 2.5,
    moving_average_window: 12,  // Changed to 12 per API MPMS 13.2
    evaluation_range_days: 90,
    proving_frequency_days: 30,
    show_individual_points: true,
    show_moving_average: true,
    show_control_limits: true,
    show_regression: false,
    show_prediction_intervals: false,
    show_confidence_intervals: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [tempSettings, setTempSettings] = useState<ChartSettings>(settings);
  const [userPreferences, setUserPreferences] = useState<any>(null);
  const [regressionData, setRegressionData] = useState<any>(null);
  // Full combo (meter/commodity + model + actuals) for the embedded detailed chart.
  const [regressionCombo, setRegressionCombo] = useState<any>(null);
  const [regressionLoading, setRegressionLoading] = useState(false);
  // Why the regression is / isn't usable, so the UI can explain instead of just greying out.
  const [regressionInfo, setRegressionInfo] = useState<{ status: string; r2?: number; n?: number } | null>(null);
  // Regression overlay mode: 'full' = in-sample (all provings); 'recent' = train
  // on history and predict the most recent 10 provings.
  const [regressionMode, setRegressionMode] = useState<'full' | 'recent' | 'walkforward' | 'ensemble'>('ensemble');
  // Per-session overrides for the two configurable regression knobs (null = use the
  // persisted user setting). Mirrors the Regression Utility's Recent/Project controls.
  const [regressionRecentN, setRegressionRecentN] = useState<number | null>(null);
  const [regressionProjection, setRegressionProjection] = useState<number | null>(null);
  // Which chart fills the main area: the control chart (MFCC + overlay) or the full
  // regression-detail view. A toggle swaps them in place instead of stacking + scroll.
  const [chartView, setChartView] = useState<'control' | 'regression'>('control');
  // Zoom/scroll window, kept in state (controlled Brush) so re-renders — switching
  // regression mode, toggling settings, regression data loading in — don't reset it
  // to the full history. Reset when the meter changes (see effect below).
  const [brushRange, setBrushRange] = useState<[number, number] | null>(null);
  // Baseline maintenance markers (shown at the bottom axis), and the one a user clicked.
  const [maintenanceMarkers, setMaintenanceMarkers] = useState<any[]>([]);
  const [selectedMaintenance, setSelectedMaintenance] = useState<any>(null);

  // Fetch user preferences
  const fetchUserPreferences = useCallback(async () => {
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/preferences`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.data && response.data.preferences) {
        setUserPreferences(response.data.preferences);
        // Mirror the display-only unit system so label helpers read it synchronously.
        const us = response.data.preferences.unit_system;
        if (us === 'metric' || us === 'imperial') setUnitSystem(us);
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
      // Use defaults if preferences fetch fails
      setUserPreferences({
        bias_threshold: 3,
        spike_threshold: 0.15,
        enable_bias_detection: true
      });
    }
  }, [getIdTokenClaims]);

  // Fetch meter history and statistics
  const fetchMeterHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/history`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = response.data;
      if (data.history && data.statistics) {
        // Process data for chart
        const processedData = data.history.map((point: MeterFactorData, index: number) => {
          const date = new Date(point.proving_timestamp);
          return {
            date: format(date, 'MMM dd, yyyy'),
            timestamp: date.getTime(),
            proving_timestamp: point.proving_timestamp, // Keep original timestamp for calculations
            meter_factor: point.meter_factor,
            moving_average: data.statistics.moving_average[index] || null,
            // Prefer per-point (maintenance-stepped) fixed limits so each proving is
            // judged against the baseline in effect at its time; fall back to the
            // single summary limits for older data that lacks the per-point series.
            fixed_ucl: (point as any).fixed_ucl ?? data.statistics.fixed_control_limits.ucl,
            fixed_lcl: (point as any).fixed_lcl ?? data.statistics.fixed_control_limits.lcl,
            fixed_center: (point as any).fixed_center ?? data.statistics.fixed_control_limits.center,
            // Use per-point floating limits if available, otherwise fall back to global
            floating_ucl: point.floating_ucl || data.statistics.floating_control_limits.ucl,
            floating_lcl: point.floating_lcl || data.statistics.floating_control_limits.lcl,
            floating_center: point.floating_ccl || data.statistics.floating_control_limits.center,
            report_id: point.report_id,
            is_spike: false,  // Will be set after sorting
            is_bias: false,   // Will be set based on bias detection
            // HM 11 repeatability: per-point value + whether it fails the gate (when enabled).
            meter_repeatability: (point as any).meter_repeatability != null && (point as any).meter_repeatability !== ''
              ? Number((point as any).meter_repeatability) : null,
            repeatability_fail: (() => {
              const rs = userPreferences?.repeatability_settings;
              const raw = (point as any).meter_repeatability;
              const rep = raw != null && raw !== '' ? Math.abs(Number(raw)) : null;
              return !!(rs?.enabled && rep != null && !Number.isNaN(rep) && rep > (rs.limit ?? 0.05));
            })(),
            // Meter-factor adjustment on THIS track (per-track attributed by the backend:
            // MF1 -> HIGH, MF2 -> LOW). has_adjustment is the boolean; adjustment_label is
            // the descriptive text (e.g. "Meter Factor 1") shown in the tooltip.
            has_adjustment: (() => {
              const ha = (point as any).has_adjustment;
              if (ha != null) return ha === true || ha === 'true' || ha === 1;
              const a = String((point as any).adjustments ?? '').trim().toUpperCase();
              return !!a && !['NO', 'NONE', 'N', 'FALSE', '0', 'NULL', 'NAN'].includes(a);
            })(),
            adjustment_label: (() => {
              const a = String((point as any).adjustments ?? '').trim();
              const up = a.toUpperCase();
              return (!a || ['NO', 'NONE', 'N', 'FALSE', '0', 'NULL', 'NAN'].includes(up)) ? '' : a;
            })()
          };
        });

        // Sort data chronologically (oldest to newest) for proper chart display
        const sortedProcessedData = processedData.sort((a: any, b: any) => {
          return a.timestamp - b.timestamp;
        });

        // Detect spikes using user's preference.
        // spike_threshold has been stored two ways: as a PERCENT (e.g. 0.15 = 0.15%) or as
        // a FRACTION (e.g. 0.0025 = 0.25%). Normalize to a fraction for the change compare.
        // (A fraction like 0.0025 must NOT be divided by 100 again -> that made 0.25% read as
        // 0.0025%, 100x too sensitive, so any tiny move looked like a spike.) Default to a
        // fraction so it's stable whether or not preferences have loaded yet.
        const _rawSpike = userPreferences?.global?.spike_threshold ?? 0.0025;
        const spikeThreshold = _rawSpike >= 0.05 ? _rawSpike / 100 : _rawSpike;
        const spikeIndices: number[] = [];
        for (let i = 1; i < sortedProcessedData.length; i++) {
          const curr = sortedProcessedData[i].meter_factor;
          const prev = sortedProcessedData[i - 1].meter_factor;
          const changePercent = Math.abs((curr - prev) / prev);

          if (changePercent > spikeThreshold) {
            sortedProcessedData[i].is_spike = true;
            sortedProcessedData[i].spike_change = (changePercent * 100).toFixed(2);
            spikeIndices.push(i);
          }
        }

        // Detect bias using user's preferences
        const biasThreshold = userPreferences?.global?.bias_threshold || 3;  // Use user preference or default to 3
        const enableBiasDetection = userPreferences?.global?.enable_bias_detection !== false;  // Default to enabled
        const controlLimitMultiplier = settings.control_limit_multiplier / 1000; // Convert to decimal

        if (enableBiasDetection) {
          for (const spikeIdx of spikeIndices) {
            const spikeValue = sortedProcessedData[spikeIdx].meter_factor;
            const spikeUcl = spikeValue + controlLimitMultiplier;
            const spikeLcl = spikeValue - controlLimitMultiplier;

            // Check if we have enough points after spike (spike + 2 more = 3 total)
            if (spikeIdx + biasThreshold - 1 <= sortedProcessedData.length) {
              let allWithinRange = true;

              // Check spike point and subsequent points
              for (let j = spikeIdx; j < spikeIdx + biasThreshold && j < sortedProcessedData.length; j++) {
                const value = sortedProcessedData[j].meter_factor;
                if (value < spikeLcl || value > spikeUcl) {
                  allWithinRange = false;
                  break;
                }
              }

              // If all points are within spike's control limits, mark them as bias
              if (allWithinRange) {
                for (let j = spikeIdx; j < spikeIdx + biasThreshold && j < sortedProcessedData.length; j++) {
                  sortedProcessedData[j].is_bias = true;
                  // Remove spike marking from bias points after the initial spike
                  if (j > spikeIdx) {
                    sortedProcessedData[j].is_spike = false;
                    delete sortedProcessedData[j].spike_change;
                  }
                }
              }
            }
          }
        }

        // Build baseline-maintenance markers. Each wrench sits at the date the
        // baseline actually took effect:
        //  - a proving the user SELECTED  -> that proving's date (reference_report_id
        //    or reference_date), which is already on the chart's x-axis;
        //  - a MANUAL value               -> the actual maintenance date recorded in
        //    the DB (which may be after the last proving, so it becomes a new x-axis
        //    point handled in chartRenderData).
        try {
          const maintResp = await axios.get(
            `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/maintenance-history`,
            { headers: { 'Authorization': `Bearer ${idToken}` } }
          );
          const events = maintResp.data?.history || [];
          const provingDates = new Set(sortedProcessedData.map((p: any) => p.date));
          const provingByReport: { [id: string]: any } = {};
          sortedProcessedData.forEach((p: any) => {
            if (p.report_id) provingByReport[p.report_id] = p;
          });
          // Convert any date/timestamp string to the chart's label + a sortable ms.
          const toLabel = (s: string) => {
            const d = new Date(String(s).length <= 10 ? `${s}T00:00:00` : s);
            return { label: format(d, 'MMM dd, yyyy'), ts: d.getTime() };
          };

          const byLabel: { [label: string]: { events: any[]; ts: number; onAxis: boolean } } = {};
          events.forEach((ev: any) => {
            let label: string | null = null;
            let ts = ev.timestamp ? new Date(ev.timestamp).getTime() : 0;
            let onAxis = false;

            if (ev.reference_report_id && provingByReport[ev.reference_report_id]) {
              // Selected proving (by id) -> its exact position on the chart.
              const p = provingByReport[ev.reference_report_id];
              label = p.date; ts = p.timestamp; onAxis = true;
            }
            if (!label && ev.reference_date) {
              // Selected proving (by stored date).
              const r = toLabel(ev.reference_date);
              label = r.label; ts = r.ts; onAxis = provingDates.has(label);
            }
            if (!label && ev.timestamp) {
              // Manual reset (or unmatched) -> the actual maintenance date.
              const r = toLabel(ev.timestamp);
              label = r.label; ts = r.ts; onAxis = provingDates.has(label);
            }
            if (!label) return;

            if (!byLabel[label]) byLabel[label] = { events: [], ts, onAxis };
            byLabel[label].events.push(ev);
            byLabel[label].ts = Math.min(byLabel[label].ts || ts, ts);
          });

          const markers = Object.keys(byLabel).map((label) => ({
            date: label,
            timestamp: byLabel[label].ts,
            onAxis: byLabel[label].onAxis,
            // Newest reset first so the dialog leads with the current baseline.
            events: byLabel[label].events.sort(
              (a, b) =>
                new Date(b.timestamp || 0).getTime() -
                new Date(a.timestamp || 0).getTime()
            ),
          }));
          setMaintenanceMarkers(markers);
        } catch (e) {
          // Maintenance markers are optional; never block the chart on them.
          setMaintenanceMarkers([]);
        }

        setChartData(sortedProcessedData);
        setStatistics(data.statistics);
      }
    } catch (err: any) {
      console.error('Error fetching meter history:', err);
      setError(err.message || 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  }, [meterProductId, settings, getIdTokenClaims]);

  // Fetch control settings
  const fetchControlSettings = useCallback(async () => {
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/control-settings`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      if (response.data.settings) {
        setSettings(response.data.settings);
        setTempSettings(response.data.settings);
      }
    } catch (err) {
      console.error('Error fetching control settings:', err);
    }
  }, [meterProductId]);

  // Fetch regression data when enabled
  const fetchRegressionData = useCallback(async () => {
    if (!settings.show_regression) {
      setRegressionData(null);
      return;
    }

    try {
      setRegressionLoading(true);
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Live regression, honoring the mode + the per-session or persisted knobs.
      const projPts = regressionProjection ?? userPreferences?.regression_settings?.projection_points ?? 0;
      const recentN = regressionRecentN ?? userPreferences?.regression_settings?.recent_predict_points ?? 10;
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${encodeURIComponent(meterProductId)}/regression`,
        {
          params: { mode: regressionMode, recent_n: recentN, projection_points: projPts },
          headers: { 'Authorization': `Bearer ${idToken}` },
        }
      );

      if (response.data && response.data.products) {
        // The backend keys `products` by the CLEAN base product_type (e.g. "KEROSENE"),
        // but a split track's id carries the flow label ("BAY 11 POS 4_KEROSENE_HIGH").
        // Try the derived key, then fall back to the single returned model — otherwise
        // every HIGH/LOW track misses the lookup and regression is silently hidden.
        const products = response.data.products || {};
        const productType = meterProductId.split('_').slice(1).join('_') || meterProductId.split('_').pop();
        const productData: any = (productType && products[productType]) || Object.values(products)[0] || null;
        setRegressionInfo(productData ? { status: productData.status, r2: productData.r_squared, n: productData.n_total ?? productData.n_observations } : null);
        // Show the overlay whenever we have a fit (good OR low-quality) — the Quality
        // and Trust chips already warn when confidence is weak. Only hide when there's
        // genuinely no model (too few provings / error).
        if (productData && ['success', 'low_quality'].includes(productData.status)) {
          setRegressionData(productData);
          setRegressionCombo(response.data.combo || null);
        } else {
          setRegressionData(null);
          setRegressionCombo(null);
        }
      } else {
        setRegressionData(null);
        setRegressionCombo(null);
        setRegressionInfo(null);
      }
    } catch (err) {
      console.error('Error fetching regression data:', err);
      setRegressionData(null);
      setRegressionCombo(null);
      setRegressionInfo(null);
    } finally {
      setRegressionLoading(false);
    }
  }, [meterProductId, settings.show_regression, regressionMode, getIdTokenClaims,
      regressionProjection, regressionRecentN,
      userPreferences?.regression_settings?.projection_points,
      userPreferences?.regression_settings?.recent_predict_points]);

  useEffect(() => {
    // Fetch user preferences first, then other data
    const loadData = async () => {
      await fetchUserPreferences();
      await Promise.all([
        fetchMeterHistory(),
        fetchControlSettings()
      ]);
    };
    loadData();
    setBrushRange(null); // new meter → start from the default (most recent) window
    setRegressionRecentN(null); // fall back to persisted knobs for the new meter
    setRegressionProjection(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meterProductId]); // Only depend on meterProductId to avoid loops

  // Fetch regression data when settings change
  useEffect(() => {
    if (settings.show_regression && chartData.length > 0) {
      fetchRegressionData();
    } else if (!settings.show_regression) {
      setRegressionData(null);
    }
  }, [settings.show_regression, chartData.length, fetchRegressionData]);

  // Merge per-point regression predictions into the chart data so the overlay is
  // a REAL regression line with leverage-aware bands (matched to points by
  // report_id, falling back to date). Points without a prediction leave a gap.
  useEffect(() => {
    if (!regressionData || !Array.isArray(regressionData.point_predictions) || chartData.length === 0) {
      return;
    }

    const byReport: Record<string, any> = {};
    const byDate: Record<string, any> = {};
    regressionData.point_predictions.forEach((p: any) => {
      if (p.report_id) byReport[p.report_id] = p;
      if (p.date) byDate[String(p.date).slice(0, 10)] = p;
    });

    let changed = false;
    const enhanced = chartData.map((point: any) => {
      const iso = point.proving_timestamp ? String(point.proving_timestamp).slice(0, 10) : '';
      const match = (point.report_id && byReport[point.report_id]) || byDate[iso] || null;
      const predicted = match ? match.predicted : null;
      const piUpper = match ? match.pi_upper : null;
      // Skip re-render once the point already carries this prediction.
      if (point.regression_predicted === predicted && point.regression_upper_prediction === piUpper) {
        return point;
      }
      changed = true;
      return {
        ...point,
        regression_predicted: predicted,
        regression_upper_prediction: piUpper,
        regression_lower_prediction: match ? match.pi_lower : null,
        regression_upper_confidence: match ? match.ci_upper : null,
        regression_lower_confidence: match ? match.ci_lower : null,
        // Interval ranges [lower, upper] so they render as a single shaded band
        // instead of two look-alike wavy lines.
        regression_pi_band: match ? [match.pi_lower, match.pi_upper] : null,
        regression_ci_band: match ? [match.ci_lower, match.ci_upper] : null,
        // Per-point transparency: which model predictors were measured at this
        // proving vs mean-imputed, and the resulting completeness weight.
        regression_present: match ? (match.predictors_present || []) : null,
        regression_imputed: match ? (match.predictors_imputed || []) : null,
        regression_completeness: match && match.completeness != null ? match.completeness : null,
      };
    });

    if (changed) {
      setChartData(enhanced);
    }
  }, [regressionData, chartData]);

  const handleSaveSettings = async () => {
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/control-settings`,
        tempSettings,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSettings(tempSettings);
      setSettingsDialogOpen(false);
      // Refresh chart with new settings
      fetchMeterHistory();
    } catch (err) {
      console.error('Error saving settings:', err);
    }
  };

  const handleExportData = () => {
    // Create CSV content
    const headers = ['Date', 'Meter Factor', 'Moving Average', 'UCL', 'LCL', 'Center'];
    const rows = chartData.map(row => [
      row.date,
      row.meter_factor,
      row.moving_average || '',
      settings.control_limit_type === 'fixed' ? row.fixed_ucl : row.floating_ucl,
      settings.control_limit_type === 'fixed' ? row.fixed_lcl : row.floating_lcl,
      settings.control_limit_type === 'fixed' ? row.fixed_center : row.floating_center
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${meterProductId}_control_chart_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Custom dot to highlight out-of-control points, spikes, and bias
  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    const ucl = settings.control_limit_type === 'fixed' ? payload.fixed_ucl : payload.floating_ucl;
    const lcl = settings.control_limit_type === 'fixed' ? payload.fixed_lcl : payload.floating_lcl;
    const isOutOfControl = payload.meter_factor > ucl || payload.meter_factor < lcl;

    // Check if this point is a spike or has bias
    const isSpike = payload.is_spike;
    const isBias = payload.is_bias;
    // HM 11 repeatability non-conformant prove (when the gate is enabled). Computed at
    // RENDER time from userPreferences (not at data-processing time) so the ring shows
    // as soon as preferences load, regardless of fetch order.
    const repeatFail = (() => {
      const rs = userPreferences?.repeatability_settings;
      const raw = payload.meter_repeatability;
      const rep = raw != null && raw !== '' ? Math.abs(Number(raw)) : null;
      return !!(rs?.enabled && rep != null && !Number.isNaN(rep) && rep > (rs.limit ?? 0.05));
    })();

    let baseDot;
    if (isSpike) {
      // Show a special indicator for spike points
      baseDot = (
        <g>
          <circle
            cx={cx}
            cy={cy}
            r={8}
            fill="#ff6b00"
            stroke={isBias ? "#9c27b0" : "#ffffff"}  // Purple outline if also bias
            strokeWidth={isBias ? 3 : 2}
          />
          {/* Lightning bolt icon for spike */}
          <text x={cx} y={cy + 3} textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
            ⚡
          </text>
        </g>
      );
    } else if (isBias) {
      // Bias without spike - show with purple outline
      baseDot = (
        <circle
          cx={cx}
          cy={cy}
          r={isOutOfControl ? 6 : 5}
          fill={isOutOfControl ? "#ff0000" : "#1976d2"}
          stroke="#9c27b0"  // Purple outline for bias
          strokeWidth={3}
        />
      );
    } else if (isOutOfControl) {
      baseDot = (
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill="#ff0000"
          stroke="#ffffff"
          strokeWidth={2}
        />
      );
    } else {
      baseDot = <circle cx={cx} cy={cy} r={4} fill="#1976d2" />;
    }

    // Meter-factor adjustment on this track (per-track attributed: MF1 -> HIGH,
    // MF2 -> LOW). A routine warning-level flag, distinct from the repeatability ring,
    // so a point can carry both. Drawn as a small gold warning triangle above the dot.
    const hasAdjustment = payload.has_adjustment === true;

    const overlays: any[] = [];
    // HM 11 repeatability: hollow dashed amber ring on a non-conformant prove.
    // Invalidated proves are excluded from the control-limit math upstream but still
    // plotted here, ringed.
    if (repeatFail) {
      overlays.push(
        <circle key="rep" cx={cx} cy={cy} r={9} fill="none" stroke="#ed6c02" strokeWidth={2} strokeDasharray="3 2" />
      );
    }
    if (hasAdjustment) {
      overlays.push(
        <path key="adj" d={`M ${cx} ${cy - 14} L ${cx - 5.5} ${cy - 5} L ${cx + 5.5} ${cy - 5} Z`}
          fill="#f9a825" stroke="#7a5c00" strokeWidth={0.75} />
      );
    }

    if (overlays.length) {
      return <g>{baseDot}{overlays}</g>;
    }

    return baseDot;
  };

  // Wrench dot for baseline-maintenance markers. Rendered as the dot of a real
  // <Line> pinned to a hidden [0,1] Y-axis near the bottom -- so Recharts computes
  // cx/cy from its own scales (version-proof, unlike the Customized axis maps that
  // changed in Recharts 3). Clicking opens the maintenance details.
  const WrenchDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || payload?.maintenance_marker == null) return null;
    return (
      <g
        transform={`translate(${cx}, ${cy})`}
        style={{ cursor: 'pointer' }}
        onClick={() =>
          setSelectedMaintenance({ date: payload.date, events: payload.maintenance_events })
        }
      >
        <title>Baseline reset — click for details</title>
        <circle cx={0} cy={0} r={10} fill="#efebe9" stroke="#8d6e63" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fontSize="12">🔧</text>
      </g>
    );
  };

  // Human-friendly predictor labels for regression transparency displays.
  const FIELD_LABELS: Record<string, string> = {
    meter_temperature: 'Temp',
    api_gravity: 'API Gravity',
    density: 'Density',
    meter_pressure: 'Pressure',
    flow_rate: 'Flow Rate',
    time_trend: 'Time trend',
  };
  const labelFields = (arr: string[] = []) =>
    arr.map((f) => FIELD_LABELS[f] || f).join(', ');

  // Regression-line dot that flags predictions built on incomplete data: an
  // amber-cored hollow ring means one or more predictors were mean-imputed
  // (missing) at that proving, so the user can see it's an estimate.
  const RegressionDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || payload?.regression_predicted == null) return null;
    const imputed: string[] = payload.regression_imputed || [];
    if (imputed.length > 0) {
      return (
        <g>
          <circle cx={cx} cy={cy} r={5} fill="#ffffff" stroke="#9c27b0" strokeWidth={1.5} />
          <circle cx={cx} cy={cy} r={2} fill="#ff9800" />
        </g>
      );
    }
    return <circle cx={cx} cy={cy} r={2.5} fill="#9c27b0" />;
  };

  // Custom tooltip that, alongside the meter factor, discloses exactly which
  // predictors were measured vs estimated for the regression at each proving.
  const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0]?.payload || {};
    const imputed: string[] = row.regression_imputed || [];
    const presentArr: string[] | null = row.regression_present || null;
    return (
      <Paper sx={{ p: 1.25, maxWidth: 280 }} elevation={4}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">
          <strong>Meter Factor:</strong> {row.meter_factor?.toFixed(4)}
        </Typography>
        {settings.control_limit_type !== 'floating' && row.fixed_ucl != null && (
          <Typography variant="caption" display="block" color="text.secondary">
            Limits (LCL / Ctr / UCL): {row.fixed_lcl?.toFixed(4)} / {row.fixed_center?.toFixed(4)} / {row.fixed_ucl?.toFixed(4)}
          </Typography>
        )}
        {(() => {
          const rs = userPreferences?.repeatability_settings;
          const raw = row.meter_repeatability;
          const rep = raw != null && raw !== '' ? Math.abs(Number(raw)) : null;
          const fail = !!(rs?.enabled && rep != null && !Number.isNaN(rep) && rep > (rs.limit ?? 0.05));
          return fail ? (
            <Typography variant="caption" display="block" sx={{ color: '#ed6c02', fontWeight: 'medium' }}>
              ⚠ Repeatability {rep != null ? `${rep.toFixed(3)}%` : ''} — failed HM 11 §7.3 (re-prove)
            </Typography>
          ) : null;
        })()}
        {row.has_adjustment === true && (
          <Typography variant="caption" display="block" sx={{ color: '#b8860b', fontWeight: 'medium' }}>
            ▲ Adjustment{row.adjustment_label ? `: ${row.adjustment_label}` : ''} (meter factor adjusted this prove)
          </Typography>
        )}
        {settings.show_regression && row.regression_predicted != null && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Typography variant="body2" sx={{ color: '#9c27b0' }}>
              <strong>Regression est.:</strong> {row.regression_predicted?.toFixed(4)}
            </Typography>
            {presentArr && (
              <Typography variant="caption" display="block" color="text.secondary">
                Measured here: {presentArr.length ? labelFields(presentArr) : 'time trend only'}
              </Typography>
            )}
            {imputed.length > 0 ? (
              <Typography variant="caption" display="block" sx={{ color: '#e65100' }}>
                ⚠ Estimated from partial data — {labelFields(imputed)} missing
                {row.regression_completeness != null
                  ? ` (${Math.round(row.regression_completeness * 100)}% complete)`
                  : ''}
              </Typography>
            ) : presentArr && presentArr.length > 0 ? (
              <Typography variant="caption" display="block" sx={{ color: '#2e7d32' }}>
                ✓ All model predictors measured at this proving
              </Typography>
            ) : null}
          </>
        )}
      </Paper>
    );
  };

  // Get trend icon
  const getTrendIcon = () => {
    if (!statistics) return null;

    if (statistics.drift_detected) {
      const lastValues = chartData.slice(-5).map(d => d.meter_factor);
      const isIncreasing = lastValues.every((v, i) => i === 0 || v >= lastValues[i - 1]);

      if (isIncreasing) {
        return <TrendingUp color="warning" />;
      } else {
        return <TrendingDown color="warning" />;
      }
    }

    return <TrendingFlat color="success" />;
  };

  // Chart rows with maintenance markers folded in. Memoized so the array IDENTITY
  // is stable across re-renders — Recharts keeps the Brush zoom window in a
  // chart-level store and resets it to the full range whenever the `data` prop
  // changes reference, so a freshly-built array each render snapped the zoom back
  // to full history. (Must sit above the early returns to satisfy hook rules.)
  const projectionPts: any[] = regressionData?.projection?.points || [];
  const chartRenderData: any[] = useMemo(() => {
    let base: any[] = chartData;
    if (maintenanceMarkers.length) {
      const markerByDate: Record<string, any> = {};
      maintenanceMarkers.forEach((m) => { markerByDate[m.date] = m; });
      const provingDates = new Set(chartData.map((p: any) => p.date));

      const annotated = chartData.map((p: any) => {
        const m = markerByDate[p.date];
        return m
          ? { ...p, maintenance_marker: 0.04, maintenance_events: m.events }
          : p;
      });

      const synthetic = maintenanceMarkers
        .filter((m) => !provingDates.has(m.date))
        .map((m) => ({
          date: m.date,
          timestamp: m.timestamp,
          proving_timestamp: new Date(m.timestamp).toISOString(),
          meter_factor: null,
          maintenance_marker: 0.04,
          maintenance_events: m.events,
        }));

      base = synthetic.length
        ? [...annotated, ...synthetic].sort(
            (a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0)
          )
        : annotated;
    }

    // Append projected future provings (damped-trend forecast) as extra points to
    // the right, on their own dashed series with a widening band. Bridge the last
    // real proving so the projection line connects cleanly.
    if (projectionPts.length && base.length) {
      const bridged = base.map((p: any, i: number) =>
        i === base.length - 1
          ? { ...p, projected: p.meter_factor, proj_pi_upper: p.meter_factor, proj_pi_lower: p.meter_factor }
          : p,
      );
      const projRows = projectionPts.map((p: any) => ({
        date: p.date ? format(new Date(p.date), 'MMM dd, yyyy') : '',
        proving_timestamp: p.date,
        timestamp: p.date ? new Date(p.date).getTime() : 0,
        meter_factor: null,
        projected: p.projected,
        proj_pi_upper: p.pi_upper,
        proj_pi_lower: p.pi_lower,
        proj_band: [p.pi_lower, p.pi_upper],
        is_projection: true,
      }));
      return [...bridged, ...projRows];
    }
    return base;
  }, [chartData, maintenanceMarkers, projectionPts]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const currentUCL = settings.control_limit_type === 'fixed'
    ? statistics?.fixed_control_limits.ucl
    : statistics?.floating_control_limits.ucl;

  const currentLCL = settings.control_limit_type === 'fixed'
    ? statistics?.fixed_control_limits.lcl
    : statistics?.floating_control_limits.lcl;

  const currentCenter = settings.control_limit_type === 'fixed'
    ? statistics?.fixed_control_limits.center
    : statistics?.floating_control_limits.center;

  // Annotate the chart data with a maintenance-marker value for points whose date
  // a baseline was set to. The value (0.04) lives on a hidden [0,1] Y-axis so the
  // wrench sits ~4% up from the bottom without disturbing the meter-factor axis.
  // Calculate additional statistics for detail boxes
  // Sort data by timestamp to ensure proper earliest/latest
  const sortedData = [...chartData].sort((a, b) => {
    const dateA = new Date(a.proving_timestamp || a.timestamp || 0).getTime();
    const dateB = new Date(b.proving_timestamp || b.timestamp || 0).getTime();
    return dateA - dateB;
  });

  const earliestData = sortedData.length > 0 ? sortedData[0] : null;
  const latestData = sortedData.length > 0 ? sortedData[sortedData.length - 1] : null;
  const currentMeterFactor = latestData?.meter_factor;

  // Check if current MF is outside control limits
  const isOutsideControlLimits = currentMeterFactor && currentUCL && currentLCL &&
    (currentMeterFactor > currentUCL || currentMeterFactor < currentLCL);

  // Calculate deviation from center
  const currentDeviation = currentMeterFactor && currentCenter ?
    ((currentMeterFactor - currentCenter) / currentCenter * 100) : null;

  // Stable y-axis domain: computed once from the WHOLE series (meter factors,
  // control limits, regression band and the forecast level) so scrolling the
  // Brush over a subset never rescales the axis — keeping the "expected next"
  // line visually fixed instead of appearing to move.
  const yDomainRaw: [number, number] = (() => {
    const vals: number[] = [];
    chartRenderData.forEach((p: any) => {
      [p.meter_factor,
       settings.control_limit_type === 'fixed' ? p.fixed_ucl : p.floating_ucl,
       settings.control_limit_type === 'fixed' ? p.fixed_lcl : p.floating_lcl,
       p.regression_predicted, p.projected, p.proj_pi_upper, p.proj_pi_lower].forEach((v) => {
        if (typeof v === 'number' && !isNaN(v)) vals.push(v);
      });
      if (Array.isArray(p.regression_pi_band)) {
        p.regression_pi_band.forEach((v: number) => {
          if (typeof v === 'number' && !isNaN(v)) vals.push(v);
        });
      }
    });
    if (regressionData?.forecast && typeof regressionData.forecast.expected === 'number') {
      vals.push(regressionData.forecast.expected);
    }
    if (!vals.length) return [0, 1];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo) * 0.1 || 0.005;
    return [lo - pad, hi + pad];
  })();
  // Clean evenly-spaced round ticks so labels are exact on tight meter-factor domains
  // (avoids Recharts' irregular auto-ticks that made points look offset from gridlines).
  const yTicks = niceTicks(yDomainRaw[0], yDomainRaw[1]);
  const yDomain: [number, number] = yTicks.length ? [yTicks[0], yTicks[yTicks.length - 1]] : yDomainRaw;

  // Controlled Brush window: honor the user's saved range (clamped to the current
  // point count) or default to the most recent 30 provings.
  const clampBrush = (i: number) => Math.max(0, Math.min(i, Math.max(0, chartRenderData.length - 1)));
  const brushStart = brushRange ? clampBrush(brushRange[0]) : Math.max(0, chartRenderData.length - 30);
  const brushEnd = brushRange ? clampBrush(brushRange[1]) : Math.max(0, chartRenderData.length - 1);

  // Show the full regression-detail chart in place of the control chart when toggled.
  const regView = settings.show_regression && chartView === 'regression' && !!regressionCombo;

  // The API labels the model by the base product ("KEROSENE"); tag the HIGH/LOW flow so
  // the regression detail header reads "KEROSENE (HIGH)" — a distinct track, distinct model.
  const regFlowLabel = (() => {
    const last = meterProductId.split('_').pop() || '';
    return ['HIGH', 'LOW', 'MID'].includes(last) ? last : '';
  })();
  const regViewCombo = regressionCombo && regFlowLabel && !String(regressionCombo.commodity || '').includes(regFlowLabel)
    ? { ...regressionCombo, commodity: `${regressionCombo.commodity} (${regFlowLabel})` }
    : regressionCombo;

  // Calculate drift count (consecutive points ALL above/below limits OR steadily trending)
  const calculateDriftCount = () => {
    if (sortedData.length < 2) return 0;

    // Check for consecutive points ALL above or ALL below control limits
    let consecutiveOutOfControl = 0;
    let allAbove = true;
    let allBelow = true;

    for (let i = sortedData.length - 1; i >= 0; i--) {
      const point = sortedData[i];
      const ucl = settings.control_limit_type === 'fixed' ? point.fixed_ucl : point.floating_ucl;
      const lcl = settings.control_limit_type === 'fixed' ? point.fixed_lcl : point.floating_lcl;


      if (point.meter_factor > ucl) {
        allBelow = false;
        if (allAbove) {
          consecutiveOutOfControl++;
        } else {
          break;
        }
      } else if (point.meter_factor < lcl) {
        allAbove = false;
        if (allBelow) {
          consecutiveOutOfControl++;
        } else {
          break;
        }
      } else {
        // Point is within limits, stop counting
        break;
      }
    }

    // Also check for consecutive steadily increasing/decreasing
    // BUT only count if ALL points are on the same side of control limits
    let consecutiveTrending = 1;
    let trendingAbove = null; // Track if trending points are above/below limits
    let trendingBelow = null;

    for (let i = sortedData.length - 2; i >= 0; i--) {
      const curr = sortedData[i + 1].meter_factor;
      const prev = sortedData[i].meter_factor;
      const currPoint = sortedData[i + 1];
      const prevPoint = sortedData[i];

      const currUcl = settings.control_limit_type === 'fixed' ? currPoint.fixed_ucl : currPoint.floating_ucl;
      const currLcl = settings.control_limit_type === 'fixed' ? currPoint.fixed_lcl : currPoint.floating_lcl;
      const prevUcl = settings.control_limit_type === 'fixed' ? prevPoint.fixed_ucl : prevPoint.floating_ucl;
      const prevLcl = settings.control_limit_type === 'fixed' ? prevPoint.fixed_lcl : prevPoint.floating_lcl;

      // Check if points cross control limits - if so, don't count as continuous trend
      const currAbove = curr > currUcl;
      const currBelow = curr < currLcl;
      const prevAbove = prev > prevUcl;
      const prevBelow = prev < prevLcl;

      // Initialize tracking on first point
      if (consecutiveTrending === 1) {
        trendingAbove = currAbove;
        trendingBelow = currBelow;
      }

      // If any point crosses limits or changes side, stop counting
      if ((currAbove && !trendingAbove) || (currBelow && !trendingBelow) ||
          (prevAbove && !trendingAbove) || (prevBelow && !trendingBelow)) {
        break; // Points are on different sides of limits, not a valid drift
      }

      // For first comparison, determine direction
      if (consecutiveTrending === 1) {
        if (curr !== prev) {
          consecutiveTrending++;
        }
      } else {
        // Check if trend continues in same direction
        const firstDiff = sortedData[sortedData.length - 1].meter_factor - sortedData[sortedData.length - 2].meter_factor;
        const currentDiff = curr - prev;

        if ((firstDiff > 0 && currentDiff > 0) || (firstDiff < 0 && currentDiff < 0)) {
          consecutiveTrending++;
        } else {
          break;
        }
      }
    }

    // Return the count only if points are all in same direction
    // For out-of-control, we already checked allAbove or allBelow
    // For trending, return that count
    // But don't combine mixed directions
    return Math.max(consecutiveOutOfControl, consecutiveTrending);
  };
  const driftCount = calculateDriftCount();
  const driftTolerance = userPreferences?.global?.drift_threshold || 5; // Use user preference

  // Calculate average days between provings
  const calculateAvgDaysBetween = () => {
    if (sortedData.length < 2) return null;
    let totalDays = 0;
    let count = 0;
    for (let i = 1; i < sortedData.length; i++) {
      const date1 = new Date(sortedData[i - 1].proving_timestamp || sortedData[i - 1].timestamp || 0);
      const date2 = new Date(sortedData[i].proving_timestamp || sortedData[i].timestamp || 0);
      // Check if dates are valid
      if (!isNaN(date1.getTime()) && !isNaN(date2.getTime())) {
        const daysDiff = Math.abs((date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24));
        totalDays += daysDiff;
        count++;
      }
    }
    return count > 0 ? Math.round(totalDays / count) : null;
  };
  const avgDaysBetween = calculateAvgDaysBetween();

  // Calculate due date for next proving (based on average interval)
  const nextProvingDate = (() => {
    if (!latestData || !avgDaysBetween) return null;
    const lastDate = new Date(latestData.proving_timestamp || latestData.timestamp || 0);
    if (isNaN(lastDate.getTime())) return null;
    return new Date(lastDate.getTime() + (avgDaysBetween * 24 * 60 * 60 * 1000));
  })();

  return (
    <>
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">
            Meter Factor Control Chart
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={1}>
            <Typography variant="body2" color="text.secondary">
              {meterName || meterProductId}
            </Typography>
            {productType && (
              <Chip
                label={regFlowLabel && !productType.toUpperCase().includes(regFlowLabel)
                  ? `${productType} (${regFlowLabel})`
                  : productType}
                size="small"
                color="primary"
              />
            )}
            {statistics && (
              <Chip
                label={`${statistics.total_provings} Provings`}
                size="small"
                variant="outlined"
              />
            )}
            {driftCount >= (userPreferences?.global?.drift_threshold || 5) && (
              <Chip
                icon={<Warning />}
                label="Drift Detected"
                size="small"
                color="warning"
              />
            )}
            {getTrendIcon()}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={fetchMeterHistory} size="small">
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={handleExportData} size="small">
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={() => setSettingsDialogOpen(true)} size="small">
            <SettingsIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Chart Indicators Legend */}
      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
          Chart Indicators:
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 24,
              height: 24,
              bgcolor: '#ff6b00',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: 12
            }}>
              ⚡
            </Box>
            <Typography variant="body2">Spike (&gt;{(() => { const r = userPreferences?.global?.spike_threshold ?? 0.0025; return (r >= 0.05 ? r : r * 100); })()}% change)</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 20,
              height: 20,
              bgcolor: '#1976d2',
              borderRadius: '50%',
              border: '3px solid #9c27b0'
            }} />
            <Typography variant="body2">Bias (meter stays at spike level)</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 20,
              height: 20,
              bgcolor: '#ff0000',
              borderRadius: '50%',
              border: '2px solid white'
            }} />
            <Typography variant="body2">Out of Control</Typography>
          </Stack>
          {userPreferences?.repeatability_settings?.enabled && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: '2px dashed #ed6c02'
              }} />
              <Typography variant="body2">Failed repeatability (HM 11 §7.3 — re-prove)</Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 0,
              height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderBottom: '12px solid #f9a825'
            }} />
            <Typography variant="body2">Meter factor adjusted</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ fontSize: 16, lineHeight: 1 }}>🔧</Box>
            <Typography variant="body2">Maintenance (baseline reset)</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 16,
              height: 16,
              bgcolor: '#1976d2',
              borderRadius: '50%'
            }} />
            <Typography variant="body2">In Control</Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Control Limit Info with API MPMS 13.2 Details */}
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <Typography variant="body2">
          UCL: <strong>{currentUCL?.toFixed(4)}</strong>
        </Typography>
        <Typography variant="body2">
          Center: <strong>{currentCenter?.toFixed(4)}</strong>
        </Typography>
        <Typography variant="body2">
          LCL: <strong>{currentLCL?.toFixed(4)}</strong>
        </Typography>
        {settings.control_limit_type !== 'fixed' && statistics?.floating_control_limits && (
          <>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={`API MPMS 13.2: Using t-distribution with ${statistics.floating_control_limits.evaluation_range_size || 12} point evaluation range, updating every ${statistics.floating_control_limits.control_range_size || 3} proofs`}>
              <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'help' }}>
                95% CI (t={statistics.floating_control_limits.t_value?.toFixed(2) || '1.96'},
                uncertainty=±{((statistics.floating_control_limits.uncertainty_range || 0) * 10000).toFixed(1)})
              </Typography>
            </Tooltip>
          </>
        )}
      </Stack>

      {/* Main Content Area with Chart and Detail Boxes */}
      <Box display="flex" gap={2}>
        {/* Chart */}
        <Box flex={1}>
          {settings.show_regression && (
            <Box sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Switch between the control chart (with regression overlay) and the full regression detail chart">
                  <ToggleButtonGroup size="small" exclusive value={chartView} onChange={(_e, v) => v && setChartView(v)}>
                    <ToggleButton value="control" sx={{ py: 0, px: 1, fontSize: '0.7rem' }}>Control Chart</ToggleButton>
                    <ToggleButton value="regression" sx={{ py: 0, px: 1, fontSize: '0.7rem' }} disabled={!regressionCombo}>Regression Detail</ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
                {!regressionCombo && regressionInfo && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {regressionInfo.status === 'insufficient_data'
                      ? `Not enough provings to fit a regression yet${regressionInfo.n ? ` (${regressionInfo.n} so far)` : ''}.`
                      : 'No regression model for this track.'}
                  </Typography>
                )}
                {chartView === 'regression' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Regression:</Typography>
                <Tooltip title={
                  regressionMode === 'recent'
                    ? 'Trains on history and predicts the most recent 10 provings'
                    : regressionMode === 'walkforward'
                    ? 'Walk-forward: predicts every proving from only the points before it (honest out-of-sample)'
                    : regressionMode === 'ensemble'
                    ? 'Ensemble: blends several models weighted by their walk-forward accuracy'
                    : 'Fits and predicts every proving in-sample'}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={regressionMode}
                    onChange={(_e, v) => v && setRegressionMode(v)}
                    disabled={regressionLoading}
                  >
                    <ToggleButton value="recent" sx={{ py: 0, px: 1, fontSize: '0.7rem' }}>Predict Recent</ToggleButton>
                    <ToggleButton value="ensemble" sx={{ py: 0, px: 1, fontSize: '0.7rem' }}>Ensemble</ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
                {regressionMode === 'recent' && (
                  <Tooltip title="How many of the most recent provings to predict (trains on everything before them)">
                    <TextField
                      select size="small" label="Recent"
                      value={regressionRecentN ?? userPreferences?.regression_settings?.recent_predict_points ?? 10}
                      onChange={(e) => setRegressionRecentN(Number(e.target.value))}
                      disabled={regressionLoading}
                      sx={{ width: 88, '& .MuiInputBase-input': { py: 0.4, fontSize: '0.72rem' } }}
                    >
                      {[5, 10, 15, 20, 25, 30].map((n) => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
                    </TextField>
                  </Tooltip>
                )}
                {regressionMode === 'ensemble' && (
                  <Tooltip title="Project this many future provings (damped-trend forecast of the out-of-sample path)">
                    <TextField
                      select size="small" label="Project"
                      value={regressionProjection ?? userPreferences?.regression_settings?.projection_points ?? 0}
                      onChange={(e) => setRegressionProjection(Number(e.target.value))}
                      disabled={regressionLoading}
                      sx={{ width: 96, '& .MuiInputBase-input': { py: 0.4, fontSize: '0.72rem' } }}
                    >
                      {[0, 1, 2, 3, 4, 5].map((n) => (<MenuItem key={n} value={n}>{n === 0 ? 'Off' : `${n} ahead`}</MenuItem>))}
                    </TextField>
                  </Tooltip>
                )}
                {regressionLoading && <CircularProgress size={14} />}
                </Box>
                )}
              </Box>
              {regressionData?.forecast && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  {regressionData.trust && regressionData.trust.level !== 'unknown' && (
                    <Tooltip title={`Out-of-sample R² ${regressionData.trust.oos_r_squared ?? 'n/a'} (walk-forward) — how reliably the model predicts unseen provings`}>
                      <Chip size="small"
                        color={regressionData.trust.level === 'reliable' ? 'success' : regressionData.trust.level === 'limited' ? 'warning' : 'error'}
                        label={`Trust: ${regressionData.trust.level}`} />
                    </Tooltip>
                  )}
                  <Typography variant="caption" sx={{ color: '#00838f', fontWeight: 600 }}>
                    Expected next: {Number(regressionData.forecast.expected).toFixed(4)}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      (95%: {Number(regressionData.forecast.pi_lower).toFixed(4)}–{Number(regressionData.forecast.pi_upper).toFixed(4)})
                      {regressionData.trust?.level === 'unreliable' ? ' · ⚠ not reliable' : ''}
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {regView ? (
            <RegressionDiagram combo={regViewCombo} height={440} />
          ) : (
          <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={chartRenderData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            domain={yDomain}
            ticks={yTicks}
            allowDataOverflow
            tickFormatter={(value) => value.toFixed(4)}
          />
          {/* Hidden axis that pins the maintenance wrenches near the bottom without
              affecting the meter-factor scale. */}
          <YAxis yAxisId="maintenance" domain={[0, 1]} hide />
          <RechartsTooltip content={<ChartTooltip />} />
          <Legend />

          {/* Control Limits */}
          {settings.show_control_limits && (
            <>
              {settings.control_limit_type !== 'floating' && (
                <>
                  {/* Maintenance-stepped fixed limits: they jump at each baseline
                      reset (aligned with the wrench markers) instead of a flat line. */}
                  <Line
                    type="stepAfter"
                    dataKey="fixed_ucl"
                    stroke="#ff0000"
                    strokeDasharray="5 5"
                    dot={false}
                    isAnimationActive={false}
                    name="Fixed UCL"
                  />
                  <Line
                    type="stepAfter"
                    dataKey="fixed_center"
                    stroke="#666666"
                    strokeDasharray="3 3"
                    dot={false}
                    isAnimationActive={false}
                    name="Center"
                  />
                  <Line
                    type="stepAfter"
                    dataKey="fixed_lcl"
                    stroke="#ff0000"
                    strokeDasharray="5 5"
                    dot={false}
                    isAnimationActive={false}
                    name="Fixed LCL"
                  />
                </>
              )}

              {settings.control_limit_type !== 'fixed' && (
                <>
                  <Line
                    type="monotone"
                    dataKey="floating_ucl"
                    stroke="#ff9800"
                    strokeDasharray="5 5"
                    dot={false}
                    name="Floating UCL (95% CI)"
                  />
                  <Line
                    type="monotone"
                    dataKey="floating_lcl"
                    stroke="#ff9800"
                    strokeDasharray="5 5"
                    dot={false}
                    name="Floating LCL (95% CI)"
                  />
                </>
              )}
            </>
          )}

          {/* Moving Average */}
          {settings.show_moving_average && (
            <Line
              type="monotone"
              dataKey="moving_average"
              stroke="#4caf50"
              strokeWidth={2}
              dot={false}
              name="Moving Average"
            />
          )}

          {/* Regression Overlay. Bands render first (behind the lines). */}
          {settings.show_regression && regressionData && (
            <>
              {/* 95% prediction interval as ONE shaded band (not two look-alike lines) */}
              {settings.show_prediction_intervals && (
                <Area
                  type="monotone"
                  dataKey="regression_pi_band"
                  stroke="none"
                  fill="#9c27b0"
                  fillOpacity={0.12}
                  isAnimationActive={false}
                  name="95% Prediction Interval"
                />
              )}
              {/* 95% confidence interval band (tighter, inside the PI) */}
              {settings.show_confidence_intervals && (
                <Area
                  type="monotone"
                  dataKey="regression_ci_band"
                  stroke="none"
                  fill="#e91e63"
                  fillOpacity={0.14}
                  isAnimationActive={false}
                  name="95% Confidence Interval"
                />
              )}
              {/* Expected-next-proving forecast level (teal, distinct from the green MA) */}
              {regressionData.forecast && (
                <ReferenceLine
                  y={regressionData.forecast.expected}
                  stroke="#00838f"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  label={{ value: 'expected next', position: 'insideTopRight', fontSize: 10, fill: '#00838f' }}
                />
              )}
              {/* Regression Line -- dots flag points built on imputed data */}
              <Line
                type="monotone"
                dataKey="regression_predicted"
                stroke="#9c27b0"
                strokeWidth={2.5}
                strokeDasharray="8 4"
                dot={<RegressionDot />}
                name={`Regression (R²=${(regressionData.r_squared ?? 0) < 0 ? '<0' : regressionData.r_squared?.toFixed(3)})`}
              />
              {/* Projected future provings (damped-trend forecast): widening band +
                  a dashed amber line with hollow diamonds, clearly not measured data. */}
              {projectionPts.length > 0 && (
                <>
                  <Area
                    type="monotone"
                    dataKey="proj_band"
                    stroke="none"
                    fill="#ed6c02"
                    fillOpacity={0.12}
                    isAnimationActive={false}
                    connectNulls
                    legendType="none"
                  />
                  <Line
                    type="monotone"
                    dataKey="projected"
                    stroke="#ed6c02"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    connectNulls
                    isAnimationActive={false}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (cx == null || cy == null || !payload?.is_projection || payload?.projected == null) {
                        return <g />;
                      }
                      return (
                        <rect x={cx - 3.5} y={cy - 3.5} width={7} height={7}
                          transform={`rotate(45 ${cx} ${cy})`}
                          fill="#fff" stroke="#ed6c02" strokeWidth={1.5} />
                      );
                    }}
                    name={`Projected (${projectionPts.length} ahead)`}
                  />
                </>
              )}
            </>
          )}

          {/* Meter Factor Points */}
          {settings.show_individual_points && (
            <Line
              type="monotone"
              dataKey="meter_factor"
              stroke="#1976d2"
              strokeWidth={2}
              dot={<CustomDot />}
              name="Meter Factor"
            />
          )}

          {/* Baseline-maintenance wrenches: rendered as dots on a hidden bottom
              axis at the proving each baseline was set to. Clickable for details. */}
          {maintenanceMarkers.length > 0 && (
            <Line
              yAxisId="maintenance"
              dataKey="maintenance_marker"
              stroke="transparent"
              legendType="none"
              isAnimationActive={false}
              dot={<WrenchDot />}
              activeDot={false}
              connectNulls={false}
            />
          )}

          {/* Zoom/scroll window: defaults to the most recent provings; drag the
              handles to zoom, drag the window to pan back through history. */}
          {chartRenderData.length > 20 && (
            <Brush dataKey="date" height={18} travellerWidth={8} stroke="#bdbdbd"
              tickFormatter={() => ''}
              startIndex={brushStart}
              endIndex={brushEnd}
              onChange={(r: any) => {
                if (r && typeof r.startIndex === 'number' && typeof r.endIndex === 'number') {
                  setBrushRange((prev) =>
                    prev && prev[0] === r.startIndex && prev[1] === r.endIndex
                      ? prev
                      : [r.startIndex, r.endIndex]);
                }
              }} />
          )}
        </ComposedChart>
          </ResponsiveContainer>
          )}
        </Box>

        {/* Detail Boxes — hidden in the regression-detail view so it fills the width */}
        {!regView && (
        <Stack spacing={1.5} sx={{ minWidth: 200 }}>
          {/* Earliest Data Point */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Earliest Data Point
            </Typography>
            <Typography variant="h6">
              {earliestData ?
                (() => {
                  const date = new Date(earliestData.proving_timestamp || earliestData.timestamp || 0);
                  return !isNaN(date.getTime()) && date.getTime() > 0 ? format(date, 'MM/dd/yy') : '-';
                })() : '-'}
            </Typography>
          </Paper>

          {/* Latest Data Point */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Latest Data Point
            </Typography>
            <Typography variant="h6">
              {latestData ?
                (() => {
                  const date = new Date(latestData.proving_timestamp || latestData.timestamp || 0);
                  return !isNaN(date.getTime()) && date.getTime() > 0 ? format(date, 'MM/dd/yy') : '-';
                })() : '-'}
            </Typography>
          </Paper>

          {/* Current MF Outside Control Limits */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Current MF Outside Control Limits
            </Typography>
            <Typography
              variant="h6"
              color={isOutsideControlLimits ? 'error' : 'success.main'}
            >
              {isOutsideControlLimits ? 'Outside' : 'Within'}
            </Typography>
          </Paper>

          {/* Current MF Deviation */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Current MF Deviation
            </Typography>
            <Typography
              variant="h6"
              color={Math.abs(currentDeviation || 0) > 0.25 ? 'warning.main' : 'text.primary'}
            >
              {currentDeviation ? `${currentDeviation > 0 ? '+' : ''}${currentDeviation.toFixed(2)}%` : '-'}
            </Typography>
          </Paper>

          {/* Current MF Drift Count / Tolerance Limit */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Current MF Drift Count / Tolerance Limit
            </Typography>
            <Typography
              variant="h6"
              color={driftCount >= driftTolerance ? 'error' : 'primary'}
            >
              {driftCount} / {driftTolerance}
            </Typography>
          </Paper>

          {/* Average Days Between Provings */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Average Days Between Provings
            </Typography>
            <Typography variant="h6" color="text.primary">
              {avgDaysBetween || '-'}
            </Typography>
          </Paper>

          {/* Due Date for Next Proving */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Due Date for Next Proving
            </Typography>
            <Typography
              variant="h6"
              color={nextProvingDate && !isNaN(nextProvingDate.getTime()) && nextProvingDate < new Date() ? 'error' : 'primary'}
            >
              {nextProvingDate && !isNaN(nextProvingDate.getTime()) ? format(nextProvingDate, 'MM/dd/yy') : '-'}
            </Typography>
          </Paper>
        </Stack>
        )}
      </Box>

      {/* Regression transparency panel: the accompanying model data + a clear
          disclosure of predictor coverage and how many points relied on
          imputed (missing) data. Shown under the control chart; the detail view
          already surfaces this inline, so it's hidden there. */}
      {settings.show_regression && regressionData && !regView && (() => {
        const preds: string[] = regressionData.predictors || [];
        const coverage: Record<string, number> = regressionData.predictor_coverage || {};
        const pts: any[] = regressionData.point_predictions || [];
        const partialPts = pts.filter((p) => (p.predictors_imputed || []).length > 0);
        const isTimeOnly = (regressionData.regression_level || 0) === 0;
        const quality = regressionData.model_quality || 'UNKNOWN';
        const qualityColor =
          quality === 'GOOD' ? 'success' : quality === 'FAIR' ? 'warning' : 'error';
        return (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#faf5fc', border: '1px solid #e1bee7', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#7b1fa2' }}>
                Regression Model
              </Typography>
              <Chip size="small" color={qualityColor as any} label={`Quality: ${quality}`} />
              <Chip size="small" variant="outlined" label={`Level ${regressionData.regression_level ?? 0}`} />
              <Tooltip title={(regressionData.r_squared ?? 0) < 0 ? 'Negative R²: predicts worse than a flat average — usually too few provings for this model. See the Trust chip.' : ''}>
                <Chip size="small" variant="outlined" label={`R² ${(regressionData.r_squared ?? 0) < 0 ? '< 0' : Number(regressionData.r_squared || 0).toFixed(3)}`} />
              </Tooltip>
              <Chip size="small" variant="outlined" label={`Adj R² ${(regressionData.adjusted_r_squared ?? 0) < 0 ? '< 0' : Number(regressionData.adjusted_r_squared || 0).toFixed(3)}`} />
              <Chip size="small" variant="outlined" label={`RMSE ${Number(regressionData.rmse || 0).toFixed(5)}`} />
              <Chip size="small" variant="outlined" label={`${regressionData.n_observations || pts.length} provings`} />
            </Stack>

            {isTimeOnly ? (
              <Typography variant="body2" color="text.secondary">
                No proving predictors (temperature, gravity, density, pressure) had
                enough coverage to model, so this is a <strong>time-trend only</strong>{' '}
                estimate. Add richer proving data to unlock a predictor-based model.
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">
                  Predictors used (with data coverage across all provings):
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5, mb: 1 }}>
                  {preds.map((f) => (
                    <Chip
                      key={f}
                      size="small"
                      label={`${FIELD_LABELS[f] || f}: ${Math.round((coverage[f] ?? 1) * 100)}%`}
                      sx={{ bgcolor: '#f3e5f5' }}
                    />
                  ))}
                  {Object.keys(coverage)
                    .filter((f) => !preds.includes(f) && (coverage[f] ?? 0) > 0)
                    .map((f) => (
                      <Chip
                        key={f}
                        size="small"
                        variant="outlined"
                        label={`${FIELD_LABELS[f] || f}: ${Math.round(coverage[f] * 100)}% (too sparse)`}
                        sx={{ color: 'text.disabled', borderStyle: 'dashed' }}
                      />
                    ))}
                </Stack>
              </>
            )}

            {regressionData.mode === 'ensemble' && Array.isArray(regressionData.ensemble_members) && regressionData.ensemble_members.length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Blend:</Typography>
                {regressionData.ensemble_members.map((mem: any) => (
                  <Tooltip key={mem.name} title={`Out-of-sample R² ${mem.oos_r_squared ?? 'n/a'} → weight ${mem.weight}`}>
                    <Chip size="small" variant="outlined"
                      label={`${mem.name} ${Math.round((mem.weight || 0) * 100)}%`}
                      sx={{ opacity: (mem.weight || 0) > 0 ? 1 : 0.45 }} />
                  </Tooltip>
                ))}
              </Stack>
            )}

            {partialPts.length > 0 ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#fff', border: '1.5px solid #9c27b0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ff9800' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#e65100' }}>
                  {partialPts.length} of {pts.length} points were estimated with partial
                  data (marked with an amber-cored ring). Hover any point to see which
                  fields were measured vs. estimated.
                </Typography>
              </Stack>
            ) : !isTimeOnly ? (
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                ✓ Every point had all model predictors measured — no imputation used.
              </Typography>
            ) : null}
          </Box>
        );
      })()}

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chart Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <Typography variant="body2" gutterBottom>Control Limit Type</Typography>
              <Select
                value={tempSettings.control_limit_type}
                onChange={(e) => setTempSettings({ ...tempSettings, control_limit_type: e.target.value as any })}
                size="small"
              >
                <MenuItem value="fixed">Fixed</MenuItem>
                <MenuItem value="floating">Floating (95% CI)</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Control Limit Multiplier (×0.001)"
              type="number"
              value={tempSettings.control_limit_multiplier}
              onChange={(e) => setTempSettings({ ...tempSettings, control_limit_multiplier: parseFloat(e.target.value) })}
              size="small"
              fullWidth
            />

            <TextField
              label="Evaluation Range Size (Moving Average Window)"
              type="number"
              value={tempSettings.moving_average_window}
              onChange={(e) => setTempSettings({ ...tempSettings, moving_average_window: parseInt(e.target.value) })}
              size="small"
              fullWidth
              helperText="API MPMS 13.2: Use 12 for monthly provers, 14 for per-batch"
            />


            <TextField
              label={`Average Annual Flow (${unitLabels().volYear})`}
              type="number"
              value={tempSettings.average_annual_flow || 0}
              onChange={(e) => setTempSettings({ ...tempSettings, average_annual_flow: parseFloat(e.target.value) || 0 })}
              size="small"
              fullWidth
              helperText={`Annual flow rate through this meter in ${unitLabels().volYear}`}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.show_individual_points}
                  onChange={(e) => setTempSettings({ ...tempSettings, show_individual_points: e.target.checked })}
                />
              }
              label="Show Individual Points"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.show_moving_average}
                  onChange={(e) => setTempSettings({ ...tempSettings, show_moving_average: e.target.checked })}
                />
              }
              label="Show Moving Average"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.show_control_limits}
                  onChange={(e) => setTempSettings({ ...tempSettings, show_control_limits: e.target.checked })}
                />
              }
              label="Show Control Limits"
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Regression Analysis</Typography>
            {(() => {
              const minRegPoints =
                userPreferences?.regression_settings?.minimum_data_points
                ?? userPreferences?.global?.regression_settings?.minimum_data_points
                ?? 20;
              const enoughForReg = chartData.length >= minRegPoints;
              return (
                <>
                  {!enoughForReg && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Not enough proving report samples to create a regression based on current
                      settings ({chartData.length} of {minRegPoints} required). Add more provings
                      or lower the minimum in regression settings to enable these options.
                    </Alert>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enoughForReg && tempSettings.show_regression}
                        disabled={!enoughForReg}
                        onChange={(e) => setTempSettings({ ...tempSettings, show_regression: e.target.checked })}
                      />
                    }
                    label="Show Regression Line"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tempSettings.show_prediction_intervals}
                        onChange={(e) => setTempSettings({ ...tempSettings, show_prediction_intervals: e.target.checked })}
                        disabled={!enoughForReg || !tempSettings.show_regression}
                      />
                    }
                    label="Show Prediction Intervals (95%)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tempSettings.show_confidence_intervals}
                        onChange={(e) => setTempSettings({ ...tempSettings, show_confidence_intervals: e.target.checked })}
                        disabled={!enoughForReg || !tempSettings.show_regression}
                      />
                    }
                    label="Show Confidence Intervals"
                  />
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Baseline maintenance details */}
      <Dialog
        open={Boolean(selectedMaintenance)}
        onClose={() => setSelectedMaintenance(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          🔧 Baseline Maintenance — {selectedMaintenance?.date}
        </DialogTitle>
        <DialogContent dividers>
          {selectedMaintenance?.events?.map((ev: any, idx: number) => (
            <Box key={ev.maintenance_id || idx} sx={{ mb: idx < selectedMaintenance.events.length - 1 ? 2.5 : 0 }}>
              {selectedMaintenance.events.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  {idx === 0 ? 'Most recent reset' : `Earlier reset #${selectedMaintenance.events.length - idx}`}
                </Typography>
              )}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 0.5 }}>
                <Chip
                  size="small"
                  label={`${Number(ev.baseline_before).toFixed(4)} → ${Number(ev.baseline_after).toFixed(4)}`}
                  color="primary"
                />
                <Typography variant="body2" color="text.secondary">
                  {ev.reset_method === 'recent_proving' ? 'Set to proving value' : 'Manual value'}
                </Typography>
              </Stack>
              <Typography variant="body2">
                <strong>When set:</strong>{' '}
                {ev.timestamp ? format(new Date(ev.timestamp), 'MMM dd, yyyy h:mm a') : '—'}
              </Typography>
              {ev.performed_by && (
                <Typography variant="body2">
                  <strong>By:</strong> {ev.performed_by}
                </Typography>
              )}
              {ev.reason && (
                <Typography variant="body2">
                  <strong>Reason:</strong> {ev.reason}
                </Typography>
              )}
              {idx < selectedMaintenance.events.length - 1 && <Divider sx={{ mt: 1.5 }} />}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMaintenance(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>

      {/* Cal Insights Section */}
      <CalMeterInsights
        meterId={meterProductId.split('_')[0]}
        productType={meterProductId.split('_').slice(1).join('_')}
        chartData={sortedData}
        statistics={statistics}
        settings={settings}
        repeatabilitySettings={userPreferences?.repeatability_settings}
        driftDetected={driftCount >= (userPreferences?.global?.drift_threshold || 5)}
        spikeDetected={sortedData.some(d => d.is_spike)}
        biasDetected={sortedData.some(d => d.is_bias)}
      />
    </>
  );
};

export default MeterFactorChart;